import {IActivityThemeImageDrawOptionImage, IActivityThemeImageDrawOptionText} from "@/model/activity";
import {computed, reactive, watch} from "vue";
import {Rule, useForm} from "ant-design-vue/es/form";
import {IFormState} from "@/lib/types";
import {api} from "@/lib/api";
import {message, UploadFile} from "ant-design-vue";
import {UploadChangeParam} from "ant-design-vue/es/upload/interface";
import {IThemeEditorImage, Theme} from "@/views/editor/service/theme";

export class ThemeCategory {
    readonly active = reactive({
        x: 0,
        y: 0,
        images: [] as IThemeEditorImage[],
        rules: {} as Record<string, Rule[]>,
        info: {} as { [key: string]: string },
        uploader: {data: {} as { [key: string]: string }, loading: false},
        watcher: {timer: 0}
    })
    form: IFormState
    draws = computed(() => this.active.images[this.active.y].image.draws.filter(i => i.is_public))

    constructor(protected theme: Theme, x: number) {
        this.active.x = x
        this.active.y = theme.editor.meta.theme.keys[x]
        this.active.images = this.warp(theme.editor.meta.theme.images[x]
            .map((i, ii) => ({image: i, preview: theme.active.categories[x].previews[ii]})))
        this.updateRules()

        this.form = useForm(this.draws.value, this.active.rules);
        this.active.info['image'] = ['', '宽高比：5:4', '宽：720px，高：1280px'][x]
        watch(this.draws.value, () => this.onDrawsChange())
        this.init()
    }

    private onDrawsChange() {
        if (this.active.watcher.timer) clearTimeout(this.active.watcher.timer);
        this.active.watcher.timer = setTimeout(() => {
            this.imagePreview(this.active.y)
        }, 3000) as unknown as number;
    }

    async init() {
        if (this.active.images.length == 1) return;
        for (const i of this.active.images) {
            const k = this.active.images.indexOf(i);
            if (k == this.theme.editor.meta.theme.keys[this.active.x]) i.preview = this.warp(this.theme.active.categories[this.active.x].previews[k])
            else await this.imagePreview(k)
        }
    }

    private warp(a: any): any {
        return JSON.parse(JSON.stringify(a));
    }

    onSettingSwitch(y: number) {
        this.active.y = y
        this.updateRules()
        this.form = useForm(this.draws.value, this.active.rules);
    }

    private updateRules(): void {
        this.active.rules = {}
        if (!this.theme.editor.meta.theme.theme_id) this.active.rules['image'] = [{
            required: true, validator: (_, v: string) => {
                return v != '' && v != this.theme.noPic ? Promise.resolve() : Promise.reject('图片必传')
            }
        }]
        this.draws.value.forEach((i, k) => {
            if (i.type == 'url_image') {
                const a = i.option as IActivityThemeImageDrawOptionImage
                this.imageInfo(a.image_url).then(info => this.active.info[a.image_url] = info)
            }
            this.active.rules[k] = [{
                required: true,
                validator: () => {
                    if (i.type == 'text') {
                        const a = i.option as IActivityThemeImageDrawOptionText
                        return a.text.length < a.length_min ? Promise.reject(`至少${a.length_min}个字`) : Promise.resolve()
                    } else {
                        const a = i.option as IActivityThemeImageDrawOptionImage
                        return a.image_url == '' ? Promise.reject(`${a.title}必传`) : Promise.resolve()
                    }
                }
            }]
        })
    }

    async imagePreview(y: number): Promise<unknown> {
        if (this.active.images[y].image.draws.length == 0) return Promise.resolve()
        else {
            this.active.images[y].preview.loading = true;
        }

        return api<unknown, Blob>({
            url: `user/${this.theme.editor.userAgent.meta.account.agent_id}/activity/image/preview`,
            method: 'POST',
            data: {
                activity: this.theme.editor.meta,
                branch_id: this.theme.editor.active.preview.branch_id,
                image: this.active.images[y].image
            },
            responseType: 'blob'
        }).then((b: Blob) => {
            this.active.images[y].preview.preview = URL.createObjectURL(b);
        }).finally(() => {
            this.active.images[y].preview.loading = false;
        });
    }

    onSettingConfirm() {
        this.form.validate().then(() => {
            this.theme.editor.meta.theme.keys[this.active.x] = this.active.y
            this.theme.editor.meta.theme.images[this.active.x][this.active.y] = this.active.images[this.active.y].image
            this.theme.active.categories[this.active.x].previews[this.active.y] = this.active.images[this.active.y].preview
            this.theme.active.open = false
        }).catch(err => {
            console.log('err:', err)
        })
    }

    imageInfo(imgUrl: string): Promise<string> {
        return new Promise((resolve, reject) => {
            const i = new Image()
            i.src = imgUrl
            i.onload = () => {
                resolve(`宽：${i.width}px，高：${i.height}px，格式：png`)
            }
            i.onerror = reject
        })
    }

    onImageUploadChange(a: UploadChangeParam) {
        switch (a.file.status) {
            case "uploading":
                this.active.uploader.loading = true
                break
            case 'done':
                this.active.uploader.loading = false
                this.active.images[this.active.y].image.image_url = `https://static.pms.guandb.cn/${this.active.uploader.data.key}`
                this.active.images[this.active.y].preview.preview = `https://static.pms.guandb.cn/${this.active.uploader.data.key}`
                break
        }
    }

    async beforeUpload(f: UploadFile): Promise<UploadFile> {
        if (this.active.x > 0) {
            const i = await this.file2image(f)
            if (this.active.x == 1 && i.width / i.height != 5 / 4) {
                message.warning('图宽高比，必须是：5:4')
                return Promise.reject('图宽高比，必须是：5:4')
            }
            if (this.active.x == 2 && (i.width != 720 || i.height != 1280)) {
                console.log(i.width, i.height)
                message.warning('图片宽必须是720px，高必须是1280px')
                return Promise.reject('图片宽必须是720px，高必须是1280px')
            }
        }
        return f
    }

    private async file2image(f: UploadFile): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            const img = new Image();
            img.onerror = () => reject('原图加载失败')
            img.onload = () => resolve(img)
            reader.onload = (ev) => typeof ev.target?.result === "string" ? img.src = ev.target.result : null
            reader.readAsDataURL(f as unknown as File);
            reader.onerror = () => reject('文件读取失败')
        })
    }

}